import { Suspense } from 'react';
import {
  Navigate, Route, Routes,
} from 'react-router-dom';
import RouteChangeTracker from '../util/RouteChangeTracker';
import { isAperioHost } from '../util/aperioTracking';
import lazyRoute from '../util/lazyRoute';
import StepNavigationTracker from '../util/stepNavigation';
import PresetRobots from '../components/PresetRobots';
import NavBar from '../components/NavBar';
import SkipLink from '../components/SkipLink';
import MarketingFooter from '../components/MarketingFooter';
import {
  ROUTE_ACCOUNT_ACCOUNT_PASSWORD,
  ROUTE_ACCOUNT_APPOINTMENTS,
  ROUTE_ACCOUNT_CARS,
  ROUTE_ACCOUNT_GARAGE,
  ROUTE_ACCOUNT_GARAGE_DETAIL,
  ROUTE_ACCOUNT_CUSTOMER_INFORMATION,
  ROUTE_ACCOUNT_APPOINTMENTS_DETAIL,
  ROUTE_FRANCHISE_MANAGER_CREATE_CUSTOMER,
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_FORGOT_PASSWORD,
  ROUTE_AUTH_LOGIN,
  ROUTE_AUTH_LOGOUT,
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_CONFIRMATION,
  ROUTE_BOOK_LOCATION,
  ROUTE_BOOK_PAYMENT,
  ROUTE_BOOK_ROOT,
  ROUTE_BOOK_SCHEDULE,
  ROUTE_BOOK_SERVICES,
  ROUTE_FRANCHISE_MANAGER,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDERS,
  ROUTE_ROOT,
  ROUTE_SERVICE_RECORD,
  ROUTE_DOCUMENT_LINK,
  ROUTE_SHOP_INSPECTION,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDER_DETAIL,
  ROUTE_FRANCHISE_MANAGER_INVOICE_DETAIL,
  ROUTE_FRANCHISE_MANAGER_CUSTOMERS,
  ROUTE_FRANCHISE_MANAGER_CUSTOMER_DETAIL,
  ROUTE_FRANCHISE_MANAGER_PAYOUTS,
  ROUTE_FRANCHISE_TECHNICIAN_PAYOUTS,
  ROUTE_FRANCHISE_SALES_PAYOUTS,
  ROUTE_FRANCHISE_MANAGER_PAYOUT_DETAIL,
  ROUTE_FRANCHISE_MANAGER_ANALYTICS,
  ROUTE_FRANCHISE_SALES_ANALYTICS,
  ROUTE_FRANCHISE_INVENTORY,
  ROUTE_FRANCHISE_MANAGER_VANS_PAYOUTS,
  ROUTE_AUTH_SIGN_UP,

  ROUTE_FLEET_MANAGER,
  ROUTE_FLEET_SIGN_UP,
  ROUTE_FLEET_DASHBOARD,
  ROUTE_FLEET_VEHICLES,
  ROUTE_FLEET_VEHICLE_DETAIL,
  ROUTE_FLEET_SERVICE,
  ROUTE_FLEET_REPAIRS,
  ROUTE_FLEET_REPAIR_DETAIL,
  ROUTE_FLEET_ALERTS,
  ROUTE_FLEET_STAFF,
  ROUTE_FLEET_SETTINGS,
  ROUTE_FLEET_DRIVERS,
  ROUTE_DRIVER,
  ROUTE_SHOP_PORTAL,
} from '../util/constants';
import AddOns from './AddOns';
import Booking from './Booking';
import Location from './Location';
import NotFound from './NotFound';
import RequiredCustomer from './RequiredCustomer';
import RequiredFranchiseManager from './RequiredFranchiseManager';
import RequireLocation from './RequireLocation';
import RequireLoggedIn from './RequireLoggedIn';
import RequireSuccessfulBooking from './RequireSuccessfulBooking';
import Schedule from './Schedule';
import Services from './Services';
import Success from './Success';
import Form from '../components/Form';
import {
  BySkin,
  ConsumerAddress,
  ConsumerCar,
  ConsumerDay,
  ConsumerDetails,
  ConsumerOnly,
  ConsumerPlan,
  ConsumerShell,
  FleetOnly,
} from '../consumer/SkinRoutes';
import {
  CONSUMER_CAR,
  CONSUMER_DAY,
  CONSUMER_DETAILS,
  CONSUMER_PLAN,
} from '../consumer/paths';
import RequiredFleetManager from './RequiredFleetManager';
import RequiredFleetDriver from './RequiredFleetDriver';
import RequiredPartnerShop from './RequiredPartnerShop';

/*
 * Customers never download what is below (QA 2026-09-25 #14): every screen
 * outside the booking funnel is a chunk of its own, fetched when its route
 * renders - see util/lazyRoute.ts. Screens that belong together share a
 * chunk name, so a panel and its tabs arrive in one request.
 */
const ForgotPassword = lazyRoute(() => import(
  /* webpackChunkName: "auth" */ './ForgotPassword'
));
const Logout = lazyRoute(() => import(
  /* webpackChunkName: "auth" */ './Logout'
));
const Login = lazyRoute(() => import(
  /* webpackChunkName: "auth" */ './Login'
));
const SignUp = lazyRoute(() => import(
  /* webpackChunkName: "auth" */ './SignUp'
));
const FleetSignUp = lazyRoute(() => import(
  /* webpackChunkName: "auth" */ './FleetSignUp'
));
const AppointmentsInformation = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/AppointmentsInformation'
));
const CustomerInformationManagement = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/CustomerInformationManagement'
));
const UserInformation = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/UserInformation'
));
const VehicleInformation = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/VehicleInformation'
));
const MyVehicles = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/MyVehicles'
));
const MyVehicleDetail = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/MyVehicleDetail'
));
const Account = lazyRoute(() => import(
  /* webpackChunkName: "account" */ './Account'
));
const CustomerWorkOrderDetails = lazyRoute(() => import(
  /* webpackChunkName: "account" */ '../components/CustomerWorkOrderDetails'
));
const PrepayPayment = lazyRoute(() => import(
  /* webpackChunkName: "prepay" */ './PrepayPayment'
));
// The QR code on an Aperio member card lands here (no customer in the
// booking funnel ever needs it, so it is a chunk of its own).
const ServiceRecord = lazyRoute(() => import(
  /* webpackChunkName: "record" */ './ServiceRecord'
));
// The insurance / registration upload link (no login): a chunk of its own.
const DocumentUpload = lazyRoute(() => import(
  /* webpackChunkName: "document-upload" */ './DocumentUpload'
));
// An outside shop's shared-inspection link (no login): a chunk of its own.
const ShopInspection = lazyRoute(() => import(
  /* webpackChunkName: "shop-inspection" */ './ShopInspection'
));
const DriverApp = lazyRoute(() => import(
  /* webpackChunkName: "driver" */ './DriverApp'
));
const WorkOrdersInformation = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/WorkOrdersInformation'
));
const FranchiseManagerPanel = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ './FranchiseManagerPanel'
));
const WorkOrderDetails = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/WorkOrderDetails'
));
const InvoiceDetails = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/InvoiceDetails'
));
const CustomersInformation = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/CustomersInformation'
));
const FranchiseCustomerManagement = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/FranchiseCustomerManagement'
));
const PayoutReports = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/PayoutReports'
));
const PayoutReportDetails = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/PayoutReportDetails'
));
const InventoryInformation = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/InventoryInformation'
));
const PayoutVansReports = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/PayoutVansReports'
));
const AnalyticsInformation = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/AnalyticsInformation'
));
const AnalyticsSalesInformation = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/AnalyticsSalesInformation'
));
const TechnicianPayoutReports = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/TechnicianPayoutReports'
));
const SalesPayoutReports = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/SalesPayoutReports'
));
const CreateCustomer = lazyRoute(() => import(
  /* webpackChunkName: "franchise" */ '../components/CreateCustomer'
));
const FleetManagerPanel = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ './FleetManagerPanel'
));
const FleetDashboard = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetDashboard'
));
const FleetVehicleRegistry = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetVehicleRegistry'
));
const FleetServiceSchedule = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetServiceSchedule'
));
const FleetStaff = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetStaff'
));
const FleetRepairQuotes = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetRepairQuotes'
));
const FleetShopJob = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetShopJob'
));
const FleetActiveAlerts = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetActiveAlerts'
));
const FleetSettings = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetSettings'
));
const FleetDrivers = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetDrivers'
));
const FleetVehicleDetail = lazyRoute(() => import(
  /* webpackChunkName: "fleet" */ '../components/fleet/FleetVehicleDetail'
));
const ShopPortal = lazyRoute(() => import(
  /* webpackChunkName: "shop" */ './ShopPortal'
));

export default function Router() {
  return (
    <>
      {/* First tab stop on every page: past the header to <main id="main">. */}
      <SkipLink />
      <NavBar />
      <RouteChangeTracker />
      {/* Step changes open at the top; in-app Back walks history. */}
      <StepNavigationTracker />
      {/* noindex beyond the booking landing - only for a marketing-site preset. */}
      <PresetRobots />
      {/*
        A split-off screen is fetched on first visit; until it arrives the
        page keeps its height, so the footer does not jump up and back.
      */}
      <Suspense fallback={<div style={{ minHeight: '60vh' }} aria-busy="true" />}>
        <Routes>
          <Route
            path={ROUTE_ROOT}
            element={(
              // On Aperio's hosts the query string goes along: an ad or a
              // ChatGPT link to book.aperiomobility.com/?utm_source=...&_gl=...
              // must reach the booking step with its campaign and GA4 linker
              // intact. Every other host (gooil.app) redirects exactly as before.
              <Navigate
                to={{
                  pathname: ROUTE_BOOK_LOCATION,
                  search: isAperioHost() ? window.location.search : '',
                }}
              />
            )}
          />
          <Route
            path={ROUTE_AUTH_LOGIN}
            element={<Login />}
          />
          <Route
            path={ROUTE_AUTH_LOGOUT}
            element={<Logout />}
          />
          <Route
            path={ROUTE_AUTH_SIGN_UP}
            element={(
              <Form>
                <SignUp />
              </Form>
            )}
          />
          <Route
            path={ROUTE_AUTH_FORGOT_PASSWORD}
            element={<ForgotPassword />}
          />
          <Route
            path={ROUTE_SERVICE_RECORD}
            element={<ServiceRecord />}
          />
          <Route
            path={ROUTE_DOCUMENT_LINK}
            element={<DocumentUpload />}
          />
          <Route
            path={ROUTE_SHOP_INSPECTION}
            element={<ShopInspection />}
          />
          <Route
            path={ROUTE_ACCOUNT_ROOT}
            element={(
              <RequireLoggedIn>
                <Account />
              </RequireLoggedIn>
            )}
          >
            <Route
              path={ROUTE_ACCOUNT_ROOT}
              element={(
                <RequiredCustomer>
                  <Navigate
                    to={ROUTE_ACCOUNT_GARAGE}
                  />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_APPOINTMENTS}
              element={(
                <RequiredCustomer>
                  <AppointmentsInformation />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_APPOINTMENTS_DETAIL}
              element={(
                <RequiredCustomer>
                  <CustomerWorkOrderDetails />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_GARAGE}
              element={(
                <RequiredCustomer>
                  <MyVehicles />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_GARAGE_DETAIL}
              element={(
                <RequiredCustomer>
                  <MyVehicleDetail />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_CARS}
              element={(
                <RequiredCustomer>
                  <VehicleInformation />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_CUSTOMER_INFORMATION}
              element={(
                <RequiredCustomer>
                  <CustomerInformationManagement />
                </RequiredCustomer>
              )}
            />
            <Route
              path={ROUTE_ACCOUNT_ACCOUNT_PASSWORD}
              element={(
                <RequiredCustomer>
                  <UserInformation />
                </RequiredCustomer>
              )}
            />
          </Route>
          {/*
            The booking. Today's flow is the fleet skin; on the Aperio preset
            a visit may get the consumer skin instead (consumer/skin.ts):
            other screens over the same booking state and submit. Off Aperio
            the skin is always fleet, so these render exactly as before.
          */}
          <Route
            path={ROUTE_BOOK_ROOT}
            element={<BySkin fleet={<Booking />} consumer={<ConsumerShell />} />}
          >
            <Route
              path={ROUTE_BOOK_LOCATION}
              element={(
                <BySkin fleet={<Location />} consumer={<ConsumerAddress />} />
              )}
            />
            <Route
              path={ROUTE_BOOK_SERVICES}
              element={(
                <FleetOnly>
                  <RequireLocation>
                    <Services />
                  </RequireLocation>
                </FleetOnly>
              )}
            />
            <Route
              path={ROUTE_BOOK_ADD_ONS}
              element={(
                <FleetOnly>
                  <RequireLocation>
                    <AddOns />
                  </RequireLocation>
                </FleetOnly>
              )}
            />
            <Route
              path={ROUTE_BOOK_SCHEDULE}
              element={(
                <FleetOnly>
                  <RequireLocation>
                    <Schedule />
                  </RequireLocation>
                </FleetOnly>
              )}
            />
            <Route
              path={CONSUMER_CAR}
              element={(
                <ConsumerOnly>
                  <RequireLocation>
                    <ConsumerCar />
                  </RequireLocation>
                </ConsumerOnly>
              )}
            />
            <Route
              path={CONSUMER_PLAN}
              element={(
                <ConsumerOnly>
                  <RequireLocation>
                    <ConsumerPlan />
                  </RequireLocation>
                </ConsumerOnly>
              )}
            />
            <Route
              path={CONSUMER_DAY}
              element={(
                <ConsumerOnly>
                  <RequireLocation>
                    <ConsumerDay />
                  </RequireLocation>
                </ConsumerOnly>
              )}
            />
            <Route
              path={CONSUMER_DETAILS}
              element={(
                <ConsumerOnly>
                  <RequireLocation>
                    <ConsumerDetails />
                  </RequireLocation>
                </ConsumerOnly>
              )}
            />
          </Route>
          <Route
            path={ROUTE_BOOK_CONFIRMATION}
            element={(
              <RequireSuccessfulBooking>
                <Success />
              </RequireSuccessfulBooking>
            )}
          />
          {/* Prepay step: reached from the booking flow or an emailed link. */}
          <Route path={ROUTE_BOOK_PAYMENT} element={<PrepayPayment />} />
          <Route
            path={ROUTE_FRANCHISE_MANAGER}
            element={(
              <RequireLoggedIn>
                <RequiredFranchiseManager>
                  <FranchiseManagerPanel />
                </RequiredFranchiseManager>
              </RequireLoggedIn>
            )}
          >
            <Route
              path={ROUTE_FRANCHISE_MANAGER_CREATE_CUSTOMER}
              element={(
                <RequiredFranchiseManager>
                  <CreateCustomer />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER}
              element={(
                <RequiredFranchiseManager>
                  <Navigate
                    to={ROUTE_FRANCHISE_MANAGER_WORK_ORDERS}
                  />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_WORK_ORDERS}
              element={(
                <RequiredFranchiseManager>
                  <WorkOrdersInformation />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_PAYOUTS}
              element={(
                <RequiredFranchiseManager>
                  <PayoutReports />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_TECHNICIAN_PAYOUTS}
              element={(
                <RequiredFranchiseManager>
                  <TechnicianPayoutReports />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_SALES_PAYOUTS}
              element={(
                <RequiredFranchiseManager>
                  <SalesPayoutReports />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_VANS_PAYOUTS}
              element={(
                <RequiredFranchiseManager>
                  <PayoutVansReports />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_PAYOUT_DETAIL}
              element={(
                <RequiredFranchiseManager>
                  <PayoutReportDetails />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_CUSTOMERS}
              element={(
                <RequiredFranchiseManager>
                  <CustomersInformation />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_ANALYTICS}
              element={(
                <RequiredFranchiseManager>
                  <AnalyticsInformation />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_SALES_ANALYTICS}
              element={(
                <RequiredFranchiseManager>
                  <AnalyticsSalesInformation />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_INVENTORY}
              element={(
                <RequiredFranchiseManager>
                  <InventoryInformation />
                </RequiredFranchiseManager>
              )}
            />
            {/* <Route
              path={ROUTE_FRANCHISE_MANAGER_FRANCHISES}
              element={(
                <RequiredFranchiseManager>
                  <FranchisesList />
                </RequiredFranchiseManager>
              )}
            /> */}
            <Route
              path={ROUTE_FRANCHISE_MANAGER_WORK_ORDER_DETAIL}
              element={(
                <RequiredFranchiseManager>
                  <WorkOrderDetails />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_CUSTOMER_DETAIL}
              element={(
                <RequiredFranchiseManager>
                  <FranchiseCustomerManagement />
                </RequiredFranchiseManager>
              )}
            />
            <Route
              path={ROUTE_FRANCHISE_MANAGER_INVOICE_DETAIL}
              element={(
                <RequiredFranchiseManager>
                  <InvoiceDetails />
                </RequiredFranchiseManager>
              )}
            />
          </Route>
          {/* Free fleet dashboard sign-up: public, outside the fleet portal. */}
          <Route
            path={ROUTE_FLEET_SIGN_UP}
            element={(
              <Form>
                <FleetSignUp />
              </Form>
            )}
          />
          {/* A fleet driver signs vehicles out and in (phone screen). */}
          <Route
            path={ROUTE_DRIVER}
            element={(
              <RequireLoggedIn>
                <RequiredFleetDriver>
                  <DriverApp />
                </RequiredFleetDriver>
              </RequireLoggedIn>
            )}
          />
          {/* Fleet manager portal */}
          <Route
            path={ROUTE_FLEET_MANAGER}
            element={(
              <RequireLoggedIn>
                <RequiredFleetManager>
                  <FleetManagerPanel />
                </RequiredFleetManager>
              </RequireLoggedIn>
            )}
          >
            <Route index element={<FleetDashboard />} />
            <Route path={ROUTE_FLEET_DASHBOARD} element={<FleetDashboard />} />
            <Route path={ROUTE_FLEET_VEHICLES} element={<FleetVehicleRegistry />} />
            <Route path={ROUTE_FLEET_VEHICLE_DETAIL} element={<FleetVehicleDetail />} />
            <Route path={ROUTE_FLEET_SERVICE} element={<FleetServiceSchedule />} />
            <Route path={ROUTE_FLEET_REPAIRS} element={<FleetRepairQuotes />} />
            <Route path={ROUTE_FLEET_REPAIR_DETAIL} element={<FleetShopJob />} />
            <Route path={ROUTE_FLEET_ALERTS} element={<FleetActiveAlerts />} />
            <Route path={ROUTE_FLEET_STAFF} element={<FleetStaff />} />
            <Route path={ROUTE_FLEET_SETTINGS} element={<FleetSettings />} />
            <Route path={ROUTE_FLEET_DRIVERS} element={<FleetDrivers />} />
          </Route>

          {/* Partner shop portal — separate user type */}
          <Route
            path={ROUTE_SHOP_PORTAL}
            element={(
              <RequireLoggedIn>
                <RequiredPartnerShop>
                  <ShopPortal />
                </RequiredPartnerShop>
              </RequireLoggedIn>
            )}
          />

          <Route
            path="*"
            element={(<NotFound />)}
          />
        </Routes>
      </Suspense>
      {/* Renders nothing unless the tenant runs a marketing-site preset. */}
      <MarketingFooter />
    </>
  );
}
